/**
 * Branchen-Untergrenz-Stundensätze (netto, in Euro).
 *
 * Heuristische Vergleichswerte für die Bewertung des Stundensatzes in der
 * Stellungnahme. Bewusst grob gehalten — der Berater kann die Branche
 * weiter spezifizieren oder den Wert manuell überschreiben.
 *
 * Werte sind keine Tarifgehälter, sondern realistische Untergrenzen für
 * eine eigenwirtschaftlich tragfähige Selbständigkeit (Stand 2026,
 * basierend auf Befragungen aus der GoodWork-Beratungspraxis).
 *
 * Pure Module — nur Konstanten + eine reine Lookup-Funktion.
 */

/** Branchenkategorie-Schlüssel. Stabil — additiv erweitern, nie umbenennen. */
export type BranchCategory =
  | 'beratung'
  | 'coaching'
  | 'it_software'
  | 'design_kreativ'
  | 'handwerk'
  | 'pflege_soziales'
  | 'haushaltsnahe_dienstleistung'
  | 'einzelhandel'
  | 'gastronomie'
  | 'sonstiges';

interface BranchEntry {
  readonly category: BranchCategory;
  /** Untergrenz-Stundensatz netto, EUR. */
  readonly floorEuroPerHour: number;
  /** Suchbegriffe (lowercase, deutsch) — Substring-Match auf branchen-String. */
  readonly synonyms: ReadonlyArray<string>;
}

const BRANCH_FLOORS: ReadonlyArray<BranchEntry> = [
  {
    category: 'beratung',
    floorEuroPerHour: 25,
    synonyms: ['beratung', 'consulting', 'mediation', 'unternehmensberatung'],
  },
  {
    category: 'coaching',
    floorEuroPerHour: 25,
    synonyms: ['coaching', 'training', 'supervision'],
  },
  {
    category: 'it_software',
    floorEuroPerHour: 35,
    synonyms: [
      'software',
      'entwicklung',
      'programmier',
      'webentwicklung',
      'edv',
      'admin',
      ' it ',
      'it-',
    ],
  },
  {
    category: 'design_kreativ',
    floorEuroPerHour: 22,
    synonyms: ['design', 'grafik', 'foto', 'film', 'medien', 'text', 'redaktion'],
  },
  {
    category: 'handwerk',
    floorEuroPerHour: 18,
    synonyms: [
      'handwerk',
      'maler',
      'tischler',
      'schreiner',
      'sanitär',
      'elektro',
      'fliesenleger',
      'bau',
    ],
  },
  {
    category: 'pflege_soziales',
    floorEuroPerHour: 16,
    synonyms: ['pflege', 'sozial', 'betreuung', 'tagesmutter', 'kinder'],
  },
  {
    category: 'haushaltsnahe_dienstleistung',
    floorEuroPerHour: 14,
    synonyms: ['reinigung', 'haushalt', 'putz', 'haushaltsnah'],
  },
  {
    category: 'einzelhandel',
    floorEuroPerHour: 14,
    synonyms: ['einzelhandel', 'verkauf', 'laden', 'shop'],
  },
  {
    category: 'gastronomie',
    floorEuroPerHour: 14,
    synonyms: ['gastronomie', 'restaurant', 'café', 'gastro', 'imbiss'],
  },
];

/** Default-Untergrenze, wenn keine Branche identifiziert wurde. */
export const DEFAULT_FLOOR_EURO_PER_HOUR = 18;

/**
 * Liefert den vermuteten Branchen-Untergrenz-Stundensatz auf Basis eines
 * Branchen-Strings. Substring-Match (lowercase). Liefert immer einen Wert
 * — bei keinem Treffer den `DEFAULT_FLOOR_EURO_PER_HOUR`.
 */
export function inferBranchHourlyFloor(branchInput: string): {
  readonly category: BranchCategory;
  readonly floorEuroPerHour: number;
  readonly inferred: boolean;
} {
  const norm = branchInput.toLowerCase().trim();
  for (const entry of BRANCH_FLOORS) {
    if (entry.synonyms.some((s) => norm.includes(s))) {
      return {
        category: entry.category,
        floorEuroPerHour: entry.floorEuroPerHour,
        inferred: true,
      };
    }
  }
  return {
    category: 'sonstiges',
    floorEuroPerHour: DEFAULT_FLOOR_EURO_PER_HOUR,
    inferred: false,
  };
}
